import React, { useState, useEffect } from "react";
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Collapse,
    Dialog,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    MenuItem,
    IconButton,
    Typography,
} from "@mui/material";
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Autorenew as AutorenewIcon,
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import {
    getCompanyRequests,
    deleteCompanyRequest,
    reassessAll,
    getBusinesses,
    Business,
    CompanyRequest,
    CompanyRequestStatus,
} from "../../api/companyRequestsApi";
import { settingsApi } from "../../api/settingsApi";
import logger from "../../utils/logger";
import CompanyRequestForm from "./CompanyRequestForm";
import { useNavigate } from "react-router-dom";

interface Profession {
    id?: number;
    professionName: string;
    professionKey: string;
    colorHex?: string;
}

const STATUS_COLORS: Record<CompanyRequestStatus, "default" | "primary" | "success" | "warning"> = {
    DRAFT: "default",
    ACTIVE: "primary",
    FILLED: "success",
    CLOSED: "warning",
};

export default function CompanyRequestsList() {
    const [requests, setRequests] = useState<CompanyRequest[]>([]);
    const [professions, setProfessions] = useState<Profession[]>([]);
    const [loading, setLoading] = useState(false);
    const [professionsLoading, setProfessionsLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [professionFilter, setProfessionFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState<CompanyRequestStatus | "">("");
    const [businessOptions, setBusinessOptions] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
    const [startDateFrom, setStartDateFrom] = useState("");
    const [startDateTo, setStartDateTo] = useState("");
    const [endDateFrom, setEndDateFrom] = useState("");
    const [endDateTo, setEndDateTo] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState<CompanyRequest | null>(null);
    const [reassessing, setReassessing] = useState(false);
    const [reassessPolling, setReassessPolling] = useState(false);
    const [reassessToast, setReassessToast] = useState<{ open: boolean; message?: string; severity?: "success" | "warning" }>({ open: false });
    const [expandedRequests, setExpandedRequests] = useState<Set<number>>(new Set());
    const navigate = useNavigate();

    const toggleExpand = (id: number) => {
        setExpandedRequests((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleReassessAll = async () => {
        if (!window.confirm("Re-run AI matching across every DRAFT/ACTIVE role? Existing SUGGESTED matches will be replaced; CONFIRMED and REJECTED are preserved.")) {
            return;
        }
        setReassessing(true);
        try {
            await reassessAll();
            setReassessPolling(true);
        } catch (err) {
            logger.error("Reassess all failed", err);
            window.alert("Failed to start reassessment — see console for details.");
        } finally {
            setReassessing(false);
        }
    };

    // Poll list while any role is still DRAFT (the bulk processor is still working).
    useEffect(() => {
        if (!reassessPolling) return;
        let cancelled = false;
        const start = Date.now();
        const POLL_MS = 4000;
        const MAX_MS = 90_000;

        const tick = async () => {
            if (cancelled) return;
            const data = await loadRequests();
            if (cancelled) return;

            const draftRoleCount = data.reduce(
                (sum, r) => sum + (r.roles?.filter((role) => role.status === "DRAFT").length ?? 0),
                0,
            );
            if (draftRoleCount === 0) {
                setReassessPolling(false);
                setReassessToast({
                    open: true,
                    severity: "success",
                    message: "Reassessment complete — open each role to review potential matches.",
                });
                return;
            }
            if (Date.now() - start > MAX_MS) {
                setReassessPolling(false);
                setReassessToast({
                    open: true,
                    severity: "warning",
                    message: `Reassessment finished but ${draftRoleCount} role${draftRoleCount === 1 ? "" : "s"} still DRAFT — no candidates passed filtering.`,
                });
                return;
            }
            setTimeout(tick, POLL_MS);
        };

        const startTimer = setTimeout(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearTimeout(startTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reassessPolling]);

    const loadProfessions = async () => {
        setProfessionsLoading(true);
        try {
            const profs = await settingsApi.getProfessions();
            setProfessions(Array.isArray(profs) ? profs : (profs as any).data || []);
        } catch {
            logger.error("Failed to load professions");
        } finally {
            setProfessionsLoading(false);
        }
    };

    const loadBusinessOptions = async () => {
        try {
            const businesses = await getBusinesses({ limit: 100 });
            setBusinessOptions(businesses);
        } catch {
            logger.error("Failed to load businesses");
        }
    };

    const loadRequests = async (): Promise<CompanyRequest[]> => {
        setLoading(true);
        try {
            const { data, total: count } = await getCompanyRequests({
                page,
                limit,
                professionKey: professionFilter || undefined,
                status: statusFilter || undefined,
                businessId: selectedBusiness?.id || undefined,
                startDateFrom: startDateFrom || undefined,
                startDateTo: startDateTo || undefined,
                endDateFrom: endDateFrom || undefined,
                endDateTo: endDateTo || undefined,
            });
            setRequests(data);
            setTotal(count);
            return data;
        } catch {
            logger.error("Failed to load company requests");
            return [];
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProfessions();
        loadBusinessOptions();
    }, []);

    useEffect(() => {
        loadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, professionFilter, statusFilter, selectedBusiness, startDateFrom, startDateTo, endDateFrom, endDateTo]);

    const handleClearFilters = () => {
        setProfessionFilter("");
        setStatusFilter("");
        setSelectedBusiness(null);
        setStartDateFrom("");
        setStartDateTo("");
        setEndDateFrom("");
        setEndDateTo("");
        setPage(1);
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Delete this request and ALL its roles + matches?")) {
            try {
                await deleteCompanyRequest(id);
                loadRequests();
            } catch {
                logger.error("Failed to delete request");
            }
        }
    };

    // Nothing to reassess when there are no requests, or every loaded one is already FILLED/CLOSED.
    const nothingToReassess = requests.length === 0 || requests.every(
        (r) => r.derivedStatus === "FILLED" || r.derivedStatus === "CLOSED",
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <h1>Company Requests</h1>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title={
                        nothingToReassess
                            ? requests.length === 0
                                ? "No company requests to reassess. Create one to enable this."
                                : "Every request is already filled or closed — nothing to reassess. Add a new request or role to re-enable."
                            : ""
                    }>
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={reassessing || reassessPolling ? <CircularProgress size={18} /> : <AutorenewIcon />}
                                onClick={handleReassessAll}
                                disabled={reassessing || reassessPolling || nothingToReassess}
                            >
                                {reassessPolling ? "Reassessing…" : "Reassess All"}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                        setEditingRequest(null);
                        setFormOpen(true);
                    }}>
                        New Request
                    </Button>
                </Box>
            </Box>

            {reassessPolling && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setReassessPolling(false)}>
                            Stop polling
                        </Button>
                    }
                >
                    <AlertTitle>Reassessment in progress</AlertTitle>
                    Refreshing every 4s. Polling auto-stops when no role is DRAFT.
                </Alert>
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2 }}>
                        <Autocomplete
                            size="small"
                            options={businessOptions}
                            getOptionLabel={(opt) => opt.name}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            value={selectedBusiness}
                            onChange={(_, value) => {
                                setSelectedBusiness(value);
                                setPage(1);
                            }}
                            renderInput={(params) => <TextField {...params} label="Company" />}
                            noOptionsText="No companies"
                        />
                        <TextField
                            select
                            label="Profession (any role)"
                            size="small"
                            value={professionFilter}
                            onChange={(e) => {
                                setProfessionFilter(e.target.value);
                                setPage(1);
                            }}
                            disabled={professionsLoading}
                        >
                            <MenuItem value="">All Professions</MenuItem>
                            {professions.map((prof) => (
                                <MenuItem key={prof.professionKey} value={prof.professionKey}>
                                    {prof.professionName} ({prof.professionKey})
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Status (any role)"
                            size="small"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value as CompanyRequestStatus | "");
                                setPage(1);
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="FILLED">Filled</MenuItem>
                            <MenuItem value="CLOSED">Closed</MenuItem>
                        </TextField>
                        <TextField
                            type="date"
                            label="Start date — from"
                            size="small"
                            value={startDateFrom}
                            onChange={(e) => { setStartDateFrom(e.target.value); setPage(1); }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="Start date — to"
                            size="small"
                            value={startDateTo}
                            onChange={(e) => { setStartDateTo(e.target.value); setPage(1); }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="End date — from"
                            size="small"
                            value={endDateFrom}
                            onChange={(e) => { setEndDateFrom(e.target.value); setPage(1); }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="End date — to"
                            size="small"
                            value={endDateTo}
                            onChange={(e) => { setEndDateTo(e.target.value); setPage(1); }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadRequests()}>
                                Refresh
                            </Button>
                            <Button onClick={handleClearFilters}>Clear</Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <TableContainer component={Card}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <Table>
                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell sx={{ width: 48 }} />
                                <TableCell>ID</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Roles</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                        No requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((req) => {
                                    const expanded = expandedRequests.has(req.id);
                                    const roleCount = req.roles?.length ?? 0;
                                    return (
                                        <React.Fragment key={req.id}>
                                            <TableRow
                                                hover
                                                onClick={() => toggleExpand(req.id)}
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <TableCell>
                                                    <IconButton size="small" aria-label={expanded ? "Collapse roles" : "Expand roles"} tabIndex={-1}>
                                                        {expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{req.id}</TableCell>
                                                <TableCell>{req.businessName || "-"}</TableCell>
                                                <TableCell>{roleCount}</TableCell>
                                                <TableCell>{req.startDate ? new Date(req.startDate).toLocaleDateString() : "-"}</TableCell>
                                                <TableCell>{req.endDate ? new Date(req.endDate).toLocaleDateString() : "-"}</TableCell>
                                                <TableCell>
                                                    <Chip label={req.derivedStatus} color={STATUS_COLORS[req.derivedStatus]} size="small" />
                                                </TableCell>
                                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip title="View">
                                                        <Button size="small" onClick={() => navigate(`/company-requests/${req.id}`)}>View</Button>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingRequest(req);
                                                                setFormOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <Button size="small" color="error" onClick={() => handleDelete(req.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded ? undefined : "unset" }}>
                                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                                                            <Typography variant="subtitle2" gutterBottom>Roles</Typography>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Role</TableCell>
                                                                        <TableCell>Profession</TableCell>
                                                                        <TableCell align="right">Needed</TableCell>
                                                                        <TableCell align="right">Confirmed</TableCell>
                                                                        <TableCell align="right">Suggestions</TableCell>
                                                                        <TableCell>Status</TableCell>
                                                                        <TableCell align="center">Actions</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {(req.roles ?? []).map((role, idx) => (
                                                                        <TableRow key={role.id}>
                                                                            <TableCell>{idx + 1}</TableCell>
                                                                            <TableCell>{role.professionKey}</TableCell>
                                                                            <TableCell align="right">{role.numberOfWorkersNeeded ?? "-"}</TableCell>
                                                                            <TableCell align="right">{role.confirmedCount ?? 0}</TableCell>
                                                                            <TableCell align="right">
                                                                                {role.status === "FILLED" ? "N/A" : (role.matchCount ?? 0) - (role.confirmedCount ?? 0)}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Chip label={role.status} color={STATUS_COLORS[role.status]} size="small" />
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                <Button size="small" onClick={() => navigate(`/company-requests/${req.id}/roles/${role.id}`)}>
                                                                                    Open role
                                                                                </Button>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog
                open={formOpen}
                onClose={() => {
                    setFormOpen(false);
                    setEditingRequest(null);
                }}
                maxWidth="lg"
                fullWidth
            >
                <CompanyRequestForm
                    initialRequest={editingRequest}
                    onSuccess={() => {
                        setFormOpen(false);
                        setEditingRequest(null);
                        loadRequests();
                    }}
                    onCancel={() => {
                        setFormOpen(false);
                        setEditingRequest(null);
                    }}
                />
            </Dialog>

            <Snackbar
                open={reassessToast.open}
                autoHideDuration={6000}
                onClose={() => setReassessToast({ open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setReassessToast({ open: false })}
                    severity={reassessToast.severity ?? "success"}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {reassessToast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
